import { EntryActivity, EntryActivityEngineStatus } from '@zero5/activity-api';
import {
  Activity,
  CallPayload as CallPayloadResponse,
  CallData as CallDataResponse,
  DeviceMessageType,
  PaymentCalculationResultWithActivity,
  PaymentFeatures as PaymentFeaturesResponse,
  PaymentFeatureType,
} from '@zero5/client-admin-api';

import { NonNullableObject } from '@/utils/types';

import { mapActivities, mapActivity } from '../activity/mappings';

import { ActivityEngineStatus, ActivityPair, CallData, IncomingCall, PaymentFeatures } from './models';

export const mapActivityPair = (response: Activity): ActivityPair => ({
  entryActivity: response.entryActivity && mapActivity(response.entryActivity),
  exitActivity: mapActivity(response.exitActivity),
});

const mapPaymentFeatures = (features: PaymentFeaturesResponse): PaymentFeatures => {
  switch (features.type) {
    case PaymentFeatureType.Discount:
      return {
        type: 'Discount',
        discount: features.discount,
      };
    case PaymentFeatureType.DiscountMinutes:
      return {
        type: 'DiscountMinutes',
        minutes: features.minutes,
      };
    case PaymentFeatureType.SpecialPrice:
      return {
        type: 'SpecialPrice',
        priceCents: features.priceCents,
      };
  }
};

export const mapPaymentCalculationResult = (response: PaymentCalculationResultWithActivity) => ({
  ...response,
  activity: mapActivityPair(response.activity),
  paymentFeatures: response.paymentFeatures && mapPaymentFeatures(response.paymentFeatures),
});

const mapActivityEngineStatus = (status: EntryActivityEngineStatus): ActivityEngineStatus => {
  const statusToString: Record<EntryActivityEngineStatus, ActivityEngineStatus> = {
    [EntryActivityEngineStatus.DUPLICATE]: 'DUPLICATE',
    [EntryActivityEngineStatus.VALID]: 'VALID',
  };

  return statusToString[status];
};

export const mapEntryActivity = (response: EntryActivity) => ({
  data: mapActivity(response.data),
  activityEngineStatus: mapActivityEngineStatus(response.activityEngineStatus),
  isUsed: response.isUsed,
});

export const mapEntryActivities = (response: Array<EntryActivity>) => response.map(mapEntryActivity);

export const mapCallData = (callData: NonNullableObject<CallPayloadResponse>) => {
  let mappedCallData: CallData | null = null;

  switch (callData.type) {
    case DeviceMessageType.EntryFound:
      mappedCallData = {
        type: 'EntryFound',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          paymentCalculationResult: mapPaymentCalculationResult(
            callData.payload.paymentCalculationResult,
          ),
        },
      };
      break;
    case DeviceMessageType.ActivityResolved:
      mappedCallData = {
        type: 'ActivityResolved',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          paymentCalculationResult: mapPaymentCalculationResult(
            callData.payload.paymentCalculationResult,
          ),
        },
      };
      break;
    case DeviceMessageType.EntryNotFound:
      mappedCallData = {
        type: 'EntryNotFound',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          suggestedEntryActivities: mapActivities(callData.payload.suggestedEntryActivities),
          defaultFee: callData.payload.defaultFee,
        },
      };
      break;
    case DeviceMessageType.AmbiguousEntryActivities:
      mappedCallData = {
        type: 'AmbiguousEntryActivities',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          ambiguousActivities: callData.payload.ambiguousActivities,
          defaultFee: callData.payload.defaultFee,
        },
      };
      break;
    case DeviceMessageType.ErrorExitActivity:
      mappedCallData = {
        type: 'ErrorExitActivity',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          defaultFee: callData.payload.defaultFee,
        },
      };
      break;
    case DeviceMessageType.PaymentRecalculatedWithTickets:
      mappedCallData = {
        type: 'PaymentRecalculatedWithTickets',
        payload: {
          activity: mapActivityPair(callData.payload.activity),
          paymentCalculationResult: mapPaymentCalculationResult(
            callData.payload.paymentCalculationResult,
          ),
          tickets: callData.payload.tickets,
        },
      };
      break;
  }

  return mappedCallData;
};

export const mapCall = (call: CallDataResponse): IncomingCall => ({
  requestId: call.requestId,
  deviceId: call.callerInfo.payload.deviceId,
  callData: call.payload && mapCallData(call.payload),
});
